import isNumber from 'lodash/isNumber';
import React, { ComponentPropsWithoutRef } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import styled, { css, ThemeProps } from 'styled-components';
import { Theme } from '../theme';
import { font, media } from '../theme/base/mixins';
import {
  StyledAnchorComponent,
  StyledButtonComponent,
  StyledComponent
} from '../types';
import { Icon } from './Icon';

/**
 * props:
 * - primary -> to have the primary color as background
 * - secondary -> to have the secondary color as background
 * - disabled -> to have a disabled button
 * - large -> to have a large button
 * - outlined -> to have an outlined button
 * - inlined -> to have not a 100% width button
 */

export type ButtonProps = ComponentPropsWithoutRef<'button'> & {
  primary?: boolean;
  secondary?: boolean;
  outlined?: boolean;
  magazine?: boolean;
  includedInBookmarks?: boolean;
  phone?: boolean;
  icon?: boolean;
  large?: boolean;
  small?: boolean;
  inlined?: boolean;
  alternateHover?: boolean;
  rotate?: number;
  bookmark?: boolean;
};

type ButtonPropsThemed = ButtonProps & ThemeProps<Theme>;

export const Button: StyledButtonComponent<ButtonProps> = styled.button`
  display: inline-block;
  outline: none !important;
  color: ${(props: ButtonPropsThemed) =>
    props.primary || props.secondary || props.outlined
      ? props.theme.colors.white
      : props.magazine
      ? props.theme.colors.secondary
      : props.theme.colors.primary};
  background-color: ${(props: ButtonPropsThemed) =>
    props.primary
      ? props.theme.colors.primary
      : props.secondary
      ? props.theme.colors.secondary
      : props.outlined
      ? 'transparent'
      : props.theme.colors.white};
  font-family: ${(props: ButtonPropsThemed) => props.theme.defaults.fontFamily};

  ${(props: ButtonPropsThemed) =>
    props.disabled &&
    css`
      background-color: ${props.theme.colors.gray};
    `}

  ${(props: ButtonPropsThemed) =>
    props.includedInBookmarks &&
    css`
      background-color: ${props.theme.colors.accentOne};
      color: ${props.theme.colors.white};
    `}

  ${(props: ButtonPropsThemed) =>
    props.phone
      ? css`
          white-space: nowrap;
        `
      : null}

  ${(props: ButtonPropsThemed) =>
    props.icon
      ? css`
          &:before {
            font-size: ${props.large ? '3.2' : '2.2'}rem;
            vertical-align: bottom;
          }
        `
      : null}

  ${(props: ButtonPropsThemed) =>
    props.bookmark
      ? css`
          &:before {
            font-size: 1.8rem;
            vertical-align: middle;
          }
        `
      : null}

  ${(props: ButtonPropsThemed) =>
    props.large
      ? font(
          props.theme.components.button.large.fontSize,
          props.theme.components.button.large.lineHeight,
          props.theme.components.button.large.weight
        )
      : props.small
      ? font(
          props.theme.components.button.small.fontSize,
          props.theme.components.button.small.lineHeight,
          props.theme.components.button.small.weight
        )
      : font(
          props.theme.components.button.default.fontSize,
          props.theme.components.button.default.lineHeight,
          props.theme.components.button.default.weight
        )};

  padding: ${(props: ButtonPropsThemed) => (props.large ? '15px' : '10px')};

  ${media.medium`
    padding: ${(props: ButtonPropsThemed) =>
      props.large
        ? props.theme.components.button.large.padding
        : props.theme.components.button.padding};
  `}

  margin: 0;
  width: ${(props: ButtonPropsThemed) => (props.inlined ? 'auto' : '100%')};
  border: none;
  text-align: center;
  ${(props: ButtonPropsThemed) => (props.disabled ? '' : 'cursor: pointer')};
  transition: ${(props: ButtonPropsThemed) => props.theme.defaults.transition};
  box-shadow: ${(props: ButtonPropsThemed) =>
    props.outlined || props.disabled
      ? ''
      : props.large
      ? props.theme.defaults.shadowLarge
      : props.theme.defaults.shadowDefault};
  ${(props: ButtonPropsThemed) =>
    props.outlined
      ? css`
          border: 1px solid ${props.theme.colors.white};
        `
      : ''};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media print {
    padding: 0;
  }

  ${(props: ButtonPropsThemed) =>
    !props.disabled &&
    css`
      &:hover {
        ${props.alternateHover
          ? css`
              background-color: ${props.primary
                ? props.theme.colors.white
                : props.secondary
                ? props.theme.colors.white
                : props.theme.colors.primary};
              color: ${props.primary
                ? props.theme.colors.primary
                : props.secondary
                ? props.theme.colors.secondary
                : props.theme.colors.white};
            `
          : css`
              background-color: ${props.primary
                ? props.theme.colors.primaryHover
                : props.secondary
                ? props.theme.colors.secondaryHover
                : props.theme.colors.white};
              ${props.outlined &&
                css`
                  color: ${props.theme.colors.secondary};
                `};
            `};
      }
    `}

  &:hover {
    ${(props: ButtonPropsThemed) =>
      props.bookmark &&
      css`
        background-color: ${props.theme.colors.accentOne};
        color: ${props.theme.colors.white};
      `}

    ${(props: ButtonPropsThemed) =>
      props.magazine &&
      css`
        background-color: ${props.theme.colors.secondary};
        color: ${props.theme.colors.white};
      `}
  }

  ${Icon} {
    fill: currentColor;
    vertical-align: top;
    ${(props: ButtonPropsThemed) =>
      isNumber(props.rotate) &&
      css`
        transform: rotate(${props.rotate}deg);
      `};

    :last-child {
      margin-right: 0;
    }

    ${(props: ButtonPropsThemed) =>
      props.small &&
      css`
        width: 16px;
        height: 16px;

        svg {
          width: 16px;
          height: 16px;
        }
      `};
  }
`;

export const ButtonAnchor: StyledComponent<
  ButtonProps,
  HTMLAnchorElement
> = Button.withComponent('a').extend`
  text-decoration: none;
` as any; // FIXME

export const ButtonLink: StyledAnchorComponent<
  ButtonProps & LinkProps
> = ButtonAnchor.withComponent(
  ({
    inlined,
    outlined,
    small,
    large,
    primary,
    secondary,
    disabled,
    alternateHover,
    magazine,
    ...props
  }: ButtonProps & LinkProps) => <Link {...props} />
);

export const MoreButton: StyledButtonComponent<ButtonProps> = Button.extend`
  &:before {
    content: '+';
    display: inline-block;
    border: 2px solid #fff;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    margin-right: 10px;
  }
`;

export const CloseButton: StyledButtonComponent<ButtonProps> = Button.extend`
  cursor: pointer;
  background: transparent;
  margin: 0;
  padding: 0 !important;
  border: 0;
  position: absolute;
  top: 12px;
  left: 18px;
  width: 24px;
  height: 24px;
  box-shadow: none;
  color: ${(props: ThemeProps<Theme>) => props.theme.colors.black};
  font-weight: bold;

  &:hover {
    background: transparent;
  }

  > i {
    width: 100%;
    height: 100%;
  }
`;

export const TextButton = Button.extend`
  width: auto;
  box-shadow: none;
  background: none;
  padding: 0;
  display: inline-flex;
  align-items: center;
  overflow: initial;
  gap: 0.2em;

  &:hover {
    background: none;
    color: ${(props) => props.theme.colors.primaryHover};
  }

  ${media.medium`
    padding: 0;
  `}
`;
